import {
  Collapsible,
  Icon,
  IconButton,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { PropsWithChildren } from 'lib-shared-types-utils';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { AuditLogEntry } from 'apis/auditLogs';
import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';

import styles from './logEntry.module.scss';
import { isAutomaticEvent } from './logEntry.utils';

export type EntryProps = {
  entry: AuditLogEntry;
  icon?: IconNames;
  isExpandable?: boolean;
};

type LogEntryProps = {
  text: ReactNode;
  secondaryText?: ReactNode;
  actions?: ReactNode;
  /** Shown below the entry whether or not it is expanded, unlike `children`. */
  content?: ReactNode;
  onExpandedChange?: (isExpanded: boolean) => void;
  /**
   * Whether expanding is tracked as the conversation activity log's footer
   * being expanded. Off for logs of other resources.
   */
  trackExpanded?: boolean;
} & EntryProps &
  PropsWithChildren;

export const LogEntry = ({
  entry,
  text,
  secondaryText,
  actions,
  content,
  children,
  isExpandable = false,
  icon,
  onExpandedChange,
  trackExpanded = true,
}: LogEntryProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(!isExpandable);
  const eventTypeIcon = isAutomaticEvent(entry) ? 'cog' : 'user';

  useEffect(() => {
    if (isExpanded && trackExpanded) {
      ampli.conversationActivityLogFlooterExpanded();
    }
  }, [isExpanded, trackExpanded]);

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <Icon name={icon || eventTypeIcon} size={16} />
          </div>
          <div>
            <Text.Paragraph variant="3" className={styles.text}>
              {text}
            </Text.Paragraph>
            {secondaryText && (
              <Text.Paragraph variant="4" subdued className={styles.text}>
                {secondaryText}
              </Text.Paragraph>
            )}
          </div>

          {isExpandable && (
            <IconButton
              onClick={() => {
                const nextIsExpanded = !isExpanded;
                setIsExpanded(nextIsExpanded);
                onExpandedChange?.(nextIsExpanded);
              }}
              icon={isExpanded ? 'angle-up' : 'angle-down'}
              variant="tertiary"
            />
          )}

          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        <ConversationTimestamp
          shouldDateFormatPersist={true}
          time={entry.event_timestamp}
          isSecondsDisplayed={false}
        />
      </div>
      {content}
      <Collapsible isOpen={isExpanded} header={null}>
        {children}
      </Collapsible>
    </div>
  );
};
